import type { WidgetBuildInfo } from './analytics/types';
import type { WidgetConsentState } from './consent/consentState';
import type { WidgetInitialConfig } from './widgetConfig';

export const WIDGET_READY_DOM_EVENT_NAME = 'passenger-widget:ready';

type WidgetConsentStateInput = WidgetConsentState | string | null;

interface WidgetRenderOptions {
  hostElement: HTMLElement;
  container: HTMLDivElement;
  tenant: string;
  baseUrl?: string;
  initialConfig: WidgetInitialConfig;
  consentState: WidgetConsentState;
  buildInfo: WidgetBuildInfo;
}

interface StandardWidgetDependencies {
  styleSheet: CSSStyleSheet;
  buildInfo: WidgetBuildInfo;
  renderContent: (options: WidgetRenderOptions) => void;
  resolveInitialConfig: (hostElement: HTMLElement) => WidgetInitialConfig;
  resolveConsentState: (hostElement: HTMLElement) => WidgetConsentState;
  normalizeConsentState: (value: WidgetConsentStateInput) => WidgetConsentState;
  updateConsentStateAttribute: (
    hostElement: HTMLElement,
    value: WidgetConsentState,
  ) => void;
  setSentryContext: (options: {
    tenant: string;
    buildInfo: WidgetBuildInfo;
    initialConfig: WidgetInitialConfig;
  }) => void;
}

const OBSERVED_ATTRIBUTES = [
  'tenant',
  'base-url',
  'initial-tab',
  'initial-origin',
  'initial-destination',
  'departure-stop',
  'consent-state',
  'disable-my-location',
];

export function createStandardWidget(dependencies: StandardWidgetDependencies) {
  return class StandardWidget extends HTMLElement {
    private shadow: ShadowRoot | null = null;
    private container: HTMLDivElement | null = null;

    static get observedAttributes(): string[] {
      return OBSERVED_ATTRIBUTES;
    }

    get consentState(): WidgetConsentState {
      return dependencies.resolveConsentState(this);
    }

    set consentState(value: WidgetConsentStateInput) {
      dependencies.updateConsentStateAttribute(
        this,
        dependencies.normalizeConsentState(value),
      );
    }

    connectedCallback(): void {
      if (!this.shadow) {
        const shadowMode =
          this.getAttribute('shadow-mode') === 'open' ? 'open' : 'closed';
        this.shadow = this.attachShadow({ mode: shadowMode });
        this.shadow.adoptedStyleSheets = [dependencies.styleSheet];
      }

      if (!this.container) {
        this.container = document.createElement('div');
        // Keep a stable, non-module class for global foundation CSS layers.
        // CSS modules are hashed and component-scoped, so they are not suitable
        // for this cross-cutting runtime mount target.
        this.container.className = 'widget-root';
      }

      if (this.container.parentNode !== this.shadow) {
        this.shadow.appendChild(this.container);
      }

      // Fire an event, so integrators can know when the widget is ready and the shadow root is available.
      this.dispatchEvent(new Event(WIDGET_READY_DOM_EVENT_NAME));
      this.renderWidget();
    }

    attributeChangedCallback(): void {
      this.renderWidget();
    }

    private renderWidget(): void {
      if (!this.container) {
        return;
      }

      const tenant = this.getAttribute('tenant') ?? '';
      const baseUrl = this.getAttribute('base-url') ?? undefined;
      const initialConfig = dependencies.resolveInitialConfig(this);
      const consentState = dependencies.resolveConsentState(this);

      dependencies.setSentryContext({
        tenant,
        buildInfo: dependencies.buildInfo,
        initialConfig,
      });

      dependencies.renderContent({
        hostElement: this,
        container: this.container,
        tenant,
        baseUrl,
        initialConfig,
        consentState,
        buildInfo: dependencies.buildInfo,
      });
    }

    disconnectedCallback(): void {
      // Cleanup is handled by Preact's render
    }
  };
}
